<script>
import i18n from "@/i18n";
import moment from "moment-timezone";
import {userSettingStore} from "@/store/userSettingStore";
import {useVuelidate} from "@vuelidate/core";
import {helpers, required} from "@vuelidate/validators";
import {mapActions, mapState} from "pinia";
import {utilsStore} from "@/store/utilsStore";
import {profileStore} from "@/store/profileStore";
import {editProfileStore} from "@/store/editProfileStore";
import {authStore} from "@/store/authStore";
import {baseLoaderStore} from "@/store/baseLoaderStore";
import {userStore} from "@/store/userStore";
import {successMessageStore} from "@/store/successMessageStore";
import Select from "primevue/select";
import DatePicker from "primevue/datepicker";
import BasePrivacySwitcher from "@/components/BasePrivacySwitcher.vue";

export default {
  name: "ProfileEditGeneral",
  setup () {
    return { v$: useVuelidate() }
  },
  components: {
    Select,
    DatePicker,
    BasePrivacySwitcher
  },
  data() {
    return {
      firstNameVal: '',
      lastNameVal: '',
      aboutVal: '',
      genderVal: '',
      birthdayVal: '',
      webSiteVal: '',
      languageVal: '',
      timezoneVal: '',
      saveData: {},

      firstNameActive: false,
      lastNameActive: false,
      aboutActive: false,
      webSiteActive: false,

      saveBtnState: false,
      isLoaded: false,
      languageChangedState: false,
      timezoneChangedState: false
    }
  },
  validations () {
    return {
      firstNameVal: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required)
      },
      lastNameVal: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required)
      },
      birthdayVal: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required)
      },
    }},
  computed: {
    ...mapState(utilsStore, [
      'timezones',
    ]),
    ...mapState(userSettingStore,[
      'userSettingsInfo'
    ]),
    ...mapState(authStore, [
      'activeProfileInfo'
    ]),
    ...mapState(profileStore, [
      'politicianIsVerified',
    ]),
    ...mapState(userStore, [
      'userEmails'
    ]),
    memberId() {
      return this.$route.params.id;
    },
    genderSelectOptions() {
      return [
        { name: i18n.global.t('auth.signUpForm.gender'), code: 'NONE' },
        { name: i18n.global.t('auth.signUpForm.male'), code: 'MALE' },
        { name: i18n.global.t('auth.signUpForm.female'), code: 'FEMALE' }
      ]
    },
    languagesList() {
      return [
        { code: 'en', name: 'English'},
        { code: 'se', name: 'Svenska'}
      ]
    },
    timezonesList() {
      return Object.keys(this.timezones).map((key)=> {
        return {
          name: key,
          code: this.timezones[key]
        }
      })
    },
    userAgeCheck() {
      const birthday = moment(this.birthdayVal, 'YYYY-MM-DD', true);
      const age = moment().diff(birthday, 'years');

      return this.birthdayVal ? (age >= 13) : true;
    },
    memberPrimaryEmail() {
      return this.userEmails.filter((email)=> {
        return email.primary;
      })[0].email;
    }
  },
  methods: {
    ...mapActions(baseLoaderStore, [
      'showBaseLoader',
      'hideBaseLoader'
    ]),
    ...mapActions(utilsStore, [
      'getTimezones'
    ]),
    ...mapActions(editProfileStore, [
      'updateMemberProfileAction'
    ]),
    ...mapActions(profileStore, [
      'getProfileGeneralInfoAction'
    ]),
    ...mapActions(userSettingStore,[
      'updateUserSettingAction'
    ]),
    ...mapActions(successMessageStore, [
      'showSuccessMessage'
    ]),
    ...mapActions(authStore, [
      'toggleChangeEmailModalState'
    ]),
    languageChanged() {
      this.languageChangedState = true;
      this.saveBtnState = true;
    },
    timezoneChanged() {
      this.timezoneChangedState = true;
      this.saveBtnState = true;
    },
    cancel() {
      this.$router.push({ name: 'UserView', params: { id: this.memberId }});
    },
    processEditData(data) {
      this.firstNameVal = data.firstName;
      this.lastNameVal = data.lastName;
      this.aboutVal = data.about || '';
      this.genderVal = data.gender;
      this.birthdayVal = data.birthday;
      this.webSiteVal = data.webSite || ''
      this.timezoneVal = data.tmzId;
    },
    dataChanged(field) {
      Object.assign(this.saveData, field);
      this.saveBtnState = true;
    },
    async saveChanges() {
      const isFormCorrect = await this.v$.$validate()
      let readyForSave = [];

      if (this.saveBtnState) {
        this.v$.$touch();

        if (isFormCorrect) {
          this.showBaseLoader();
          this.saveBtnState = false;

          if (this.languageChangedState) {
            readyForSave.push(this.updateUserSettingAction('LANGUAGE', this.languageVal));
          }

          if (this.timezoneChangedState) {
            readyForSave.push(this.updateUserSettingAction('TIME_ZONE', this.timezoneVal));
          }

          if (Object.keys(this.saveData).length !== 0)  {
            readyForSave.push(this.updateMemberProfileAction(this.activeProfileInfo.id, this.saveData));
          }

          Promise.all(readyForSave).then(()=> {
            this.saveData = {};
            this.languageChangedState = false;
            this.timezoneChangedState = false;
            this.hideBaseLoader();
            this.showSuccessMessage(i18n.global.t('editProfile.profileHasBeenUpdated'), 500);
          });
        }
      }
    }
  },
  beforeMount() {
    this.showBaseLoader();
    this.getTimezones();
    this.getProfileGeneralInfoAction(this.memberId).then((resp) => {
      this.processEditData(resp.data);
      this.isLoaded = true;
      this.languageVal = this.userSettingsInfo['LANGUAGE'];
      this.saveBtnState = false;
      this.hideBaseLoader();
    });
  }
}
</script>

<template>
<div class="profile-edit-general">
  <div v-if="isLoaded" class="edit-profile-general-form">
    <div class="edit-profile-general-form__content">
      <div class="field-wrap">
        <label class="form-label">{{$t('editProfile.firstName')}}</label>
        <div>
          <div>
            <input type="text"
                   v-model="firstNameVal"
                   @focus="firstNameActive = true"
                   @blur="firstNameActive = false"
                   maxlength="30"
                   class="form-control"
                   :class="{'form-control--error': v$.firstNameVal.$error}"
                   :disabled="politicianIsVerified"
                   @keyup="dataChanged({'firstName': firstNameVal})"
            />
            <span v-if="firstNameActive" class="form-control__char-count">
              {{30 - firstNameVal.length}}
            </span>
          </div>
          <span v-for="error of v$.firstNameVal.$errors" :key="error.$uid"
                  class="form-control-error"
            >
            <template v-if="!firstNameActive">{{ error.$message }}</template>
          </span>
        </div>
      </div>
      <div class="field-wrap m-0">
        <label class="form-label">{{$t('editProfile.lastName')}}</label>
        <div class="d-flex flex-direction-column align-item-end">
          <div>
            <input type="text"
                   v-model="lastNameVal"
                   @focus="lastNameActive = true"
                   @blur="lastNameActive = false"
                   maxlength="30"
                   class="form-control"
                   :class="{'form-control--error': v$.lastNameVal.$error}"
                   :disabled="politicianIsVerified"
                   @keyup="dataChanged({'lastName': lastNameVal})"
            />
            <span v-if="lastNameActive" class="form-control__char-count">
              {{30 - lastNameVal.length}}
            </span>
          </div>
          <span class="form-label-info">
            {{$t('editProfile.nameChangesInfo')}}
          </span>
          <span v-for="error of v$.lastNameVal.$errors" :key="error.$uid"
                class="form-control-error"
          >
            <template v-if="!lastNameActive">
              {{ error.$message }}
            </template>
          </span>
        </div>
      </div>
      <div class="field-separator"></div>
      <div class="field-wrap field-wrap--text-area m-0">
        <label class="form-label">{{$t('editProfile.aboutYou')}}</label>
        <div>
          <div>
             <textarea v-model="aboutVal"
                       class="form-control" rows="5"
                       @focus="aboutActive = true"
                       @blur="aboutActive = false"
                       @keyup="dataChanged({'about': aboutVal})"
                       maxlength="2000"
             ></textarea>
          </div>
          <span v-if="aboutActive" class="form-control__char-count">
            {{2000 - aboutVal.length}}
          </span>
        </div>
        <base-privacy-switcher :privacy-type="'ABOUT_PRIVACY'"
                               :privacy-value="userSettingsInfo['ABOUT_PRIVACY']"
        />
      </div>
      <div class="field-separator"></div>
      <div class="field-wrap m-0">
        <label class="form-label">{{$t('editProfile.gender')}}</label>
        <Select v-model="genderVal"
                :options="genderSelectOptions"
                optionLabel="name"
                optionValue="code"
                overlayClass="sign-up-gender-select-overlay"
                class="sign-up-gender-select"
                :disabled="politicianIsVerified"
                @change="dataChanged({'gender': genderVal})"
        />
        <base-privacy-switcher :privacy-type="'GENDER_PRIVACY'"
                               :privacy-value="userSettingsInfo['GENDER_PRIVACY']"
        />
      </div>
      <div class="field-separator"></div>
      <div class="field-wrap m-0">
        <label class="form-label">{{$t('editProfile.birthday')}}</label>
        <div>
          <DatePicker v-model="birthdayVal"
                      dateFormat="dd/mm/yy"
                      :class="{'p-datepicker--error': (v$.birthdayVal.$error || !userAgeCheck)}"
                      :placeholder="$t('auth.signUpForm.selectDate')"
                      :disabled="politicianIsVerified"
                      @date-select="dataChanged({'birthday': birthdayVal})"
          />
          <span v-for="error of v$.birthdayVal.$errors" :key="error.$uid"
                class="form-control-error"
          >
            <template>{{ error.$message }}</template>
          </span>
          <span v-if="!userAgeCheck && birthdayVal"  class="form-control-error">
            {{$t('validations.ageNotValid')}}
          </span>
        </div>
        <base-privacy-switcher :privacy-type="'BIRTHDAY_PRIVACY'"
                               :privacy-value="userSettingsInfo['BIRTHDAY_PRIVACY']"
        />
      </div>
      <div class="field-separator"></div>
      <div class="field-wrap m-0">
        <label class="form-label">{{$t('editProfile.email')}}</label>
        <div>
          <input type="text"
                 :value="memberPrimaryEmail"
                 class="form-control mb-5"
                 disabled
          />
          <span class="text-link" @click="toggleChangeEmailModalState()">
            {{$t('header.message.noApprovedUser.changeEmail')}}
          </span>
        </div>
        <base-privacy-switcher :privacy-type="'EMAIL_PRIVACY'"
                               :privacy-value="userSettingsInfo['EMAIL_PRIVACY']"
        />
      </div>
      <div class="field-separator"></div>
      <div class="field-wrap field-wrap--text-area">
        <label class="form-label">{{$t('editProfile.website')}}</label>
        <div>
          <div>
             <textarea v-model="webSiteVal"
                       class="form-control" rows="5"
                       @focus="webSiteActive = true"
                       @blur="webSiteActive = false"
                       @keyup="dataChanged({'webSite': webSiteVal})"
                       maxlength="400"
             ></textarea>
          </div>
          <span v-if="webSiteActive" class="form-control__char-count">
            {{400 - webSiteVal.length}}
          </span>
        </div>
        <base-privacy-switcher :privacy-type="'WEB_SITE_PRIVACY'"
                               :privacy-value="userSettingsInfo['WEB_SITE_PRIVACY']"
        />
      </div>
      <div class="field-wrap">
        <label class="form-label">{{$t('editProfile.language')}}</label>
        <Select v-model="languageVal"
                :options="languagesList"
                @change="languageChanged()"
                optionLabel="name"
                optionValue="code"
                overlayClass="sign-up-gender-select-overlay"
                class="sign-up-gender-select"
        />
      </div>
      <div class="field-wrap">
        <label class="form-label">{{$t('editProfile.timezone')}}</label>
        <Select v-model="timezoneVal"
                :options="timezonesList"
                optionLabel="name"
                optionValue="name"
                overlayClass="sign-up-gender-select-overlay"
                class="sign-up-gender-select"
                @change="timezoneChanged()"
        />
      </div>
    </div>
    <div class="edit-profile-general-form__footer">
      <button type="button" class="secondary-btn" @click="cancel()">
        {{ $t('common.buttons.cancel') }}
      </button>
      <button class="primary-btn ml-10"
              :disabled="!saveBtnState"
              @click="saveChanges()"
      >
        {{$t('editProfile.saveChanges')}}
      </button>
    </div>
  </div>
</div>
</template>

<style lang="scss">
.profile-edit-general {
  .edit-profile-general-form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    &__content {
      padding: 40px 30px 0 30px;
    }

    &__footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      height: 50px;
      width: 100%;
      padding: 0 25px;
      background: #ccc;
      border-radius: 0 0 3px 3px;
    }

    .field-wrap {
      display: flex;
      position: relative;
      margin-bottom: 20px;
      width: 315px;

      .form-label {
        display: inline-block;
        position: relative;
        top: 8px;
        width: 100px;
        margin-right: 20px;
        font-size: 12px;
        font-family: 'Open Sans',Arial,sans-serif;
        font-weight: 700;
        text-align: right;
        color: #4f4f4f;
      }

      .form-label-info {
        max-width: 220px;
        margin-top: 5px;
        font-size: 12px;
      }

      .form-control {
        min-width: 220px;
        max-width: 220px;
        background: #fff;
        display: inline-block;
        font-size: 12px;
        font-family: 'Open Sans',Arial,sans-serif;
        border: 1px solid #cccccc;
        color: #4f4f4f;
        padding: 0 30px 0 8px;
        appearance: none;
        outline: none;


        &::placeholder {
          color: #bbbbbb;
        }
        &:focus {
          border-color: #999999;
          color: #000000;
        }
        &:disabled {
          background: #d1d1d1;
          color: rgba(0,0,0,0.4);
          cursor: default;
        }

        &input {
          height: 30px;
        }

        &__char-count {
          position: absolute;
          right: 10px;
          top: 5px;
          padding-left: 7px;
          border-left: 1px solid #ccc;
          line-height: 20px;
          font-size: 12px;
          color: #4f4f4f;
        }
        &__desc {
          position: absolute;
          left: 220px;
          font-size: 12px;
          color: #4f4f4f;
        }
        &--error {
          border-color: #fe8f18;
        }
      }

      input.form-control {
        height: 30px;
      }

      textarea.form-control {
        padding: 8px 10px 15px;
      }

      .form-control-error {
        display: block;
        margin-top: 5px;
        font-size: 12px;
        color: #fe8f18;
      }

      .p-select {
        height: 30px;
        width: 220px;
        min-width: 220px;
        border: 1px solid #cccccc;
        font-size: 12px;
        color: #4f4f4f;

        &.p-focus {
          outline: none;
          border-color: #999999;
        }

        &.p-disabled {
          background: #d1d1d1;
          color: rgba(0,0,0,0.4);
          cursor: default;
        }

        .p-select-label {
          font-size: 12px;
          color: #4f4f4f;
        }
      }

      .p-datepicker {
        height: 30px;
        width: 220px;
        min-width: 220px;
        border: none;
        font-size: 12px;
        color: #4f4f4f;

        &--error {
          .p-datepicker-input {
            border-color: #fe8f18 !important;
          }
        }

        &.p-focus {
          outline: none;
          .p-inputtext {
            background: #fff;
            border-color: #999999;
          }
        }
        .p-inputtext {
          font-size: 12px;
          outline: none;
          border-color: #cccccc;
          &::placeholder {
            color: #bbbbbb;
          }
          &:disabled {
            background: #d1d1d1;
            color: rgba(0,0,0,0.4);
            cursor: default;
          }
        }
      }

      .privacy-switcher {
        position: absolute;
        top: 5px;
        right: -25px;
      }

      &--text-area {
        .form-control__char-count {
          top: auto;
          bottom: 5px;
        }
      }
    }

    .field-separator {
      width: 100%;
      height: 1px;
      margin: 22px 0;
      background: #e2dce7;
    }

    .primary-btn {
      height: 32px;
      padding: 0 15px;
      background: #7130a0;
      font-family: 'Open Sans',Arial,sans-serif;
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      color: #fff;
      border-radius: 4px;
      border: none;
      outline: none;
      cursor: pointer;
      &:hover {
        background: #672992;
      }

      &.disabled,
      &:disabled {
        background: #909090;
        cursor: default;
      }
    }

    .secondary-btn {
      font-family: 'Open Sans',Arial,sans-serif;
      font-size: 14px;
      font-weight: 600;
      border: none;
      outline: 0;
      text-transform: uppercase;
      cursor: pointer;
      color: #7130a0;
      background: transparent;
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.sign-up-gender-select-overlay {
  .p-select-list {
    border: 1px solid #ccc;
    .p-select-option {
      background: #fff !important;
      .p-select-option-label {
        font-size: 12px;
        color: #3c3c3c;
      }
      &-selected {
        background: #e2e2e2 !important;
      }
    }
  }
}

</style>